import { api } from "@/core/api";
import type {
  AssignDeviceRequest,
  Device,
  DeviceListResponse,
  IceConfigResponse,
  RelaySessionTokenResponse,
} from "@repo/schema";

export const DEVICE_TAG = "Device";

export const deviceApi = api
  .enhanceEndpoints({ addTagTypes: [DEVICE_TAG] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** Devices assigned to the caller's organization. */
      getDevices: builder.query<DeviceListResponse, void>({
        query: () => ({ url: "/devices" }),
        providesTags: [DEVICE_TAG],
      }),

      /** Enrolled but unassigned devices. Super admins only. */
      getUnassignedDevices: builder.query<DeviceListResponse, void>({
        query: () => ({ url: "/devices/unassigned" }),
        providesTags: [DEVICE_TAG],
      }),

      assignDevice: builder.mutation<Device, { deviceId: number } & AssignDeviceRequest>({
        query: ({ deviceId, organizationId }) => ({
          url: `/devices/${deviceId}/assign`,
          method: "POST",
          body: { organizationId },
        }),
        invalidatesTags: [DEVICE_TAG],
      }),

      unassignDevice: builder.mutation<Device, { deviceId: number }>({
        query: ({ deviceId }) => ({
          url: `/devices/${deviceId}/unassign`,
          method: "POST",
        }),
        invalidatesTags: [DEVICE_TAG],
      }),

      revokeDevice: builder.mutation<Device, { deviceId: number }>({
        query: ({ deviceId }) => ({
          url: `/devices/${deviceId}/revoke`,
          method: "POST",
        }),
        invalidatesTags: [DEVICE_TAG],
      }),

      /**
       * Exchanged at the relay for a cookie. Deliberately not cached: it is a
       * credential, and re-fetching is how the cookie gets refreshed.
       */
      getRelaySessionToken: builder.query<RelaySessionTokenResponse, void>({
        query: () => ({ url: "/relay/session-token" }),
        keepUnusedDataFor: 0,
      }),

      /** Browser-side TURN credentials, replacing the hardcoded ICE servers. */
      getIceConfig: builder.query<IceConfigResponse, void>({
        query: () => ({ url: "/relay/ice" }),
      }),
    }),
  });

export const {
  useGetDevicesQuery,
  useGetUnassignedDevicesQuery,
  useAssignDeviceMutation,
  useUnassignDeviceMutation,
  useRevokeDeviceMutation,
  useLazyGetRelaySessionTokenQuery,
  useLazyGetIceConfigQuery,
} = deviceApi;
